import { useEffect, useRef, useState } from "react";
import { Play, ExternalLink, AlertTriangle } from "lucide-react";

const YT_ORIGINS = ["https://www.youtube.com", "https://www.youtube-nocookie.com"];

function BlockedNotice({ url, reason }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center" data-testid="video-blocked">
      <AlertTriangle className="h-10 w-10 text-amber-400" />
      <p className="max-w-sm text-sm text-neutral-300">{reason}</p>
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-4 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A]" data-testid="blocked-watch-youtube">
          <ExternalLink className="h-4 w-4" /> Watch on YouTube
        </a>
      )}
    </div>
  );
}

export function YouTubePlayer({ videoId, isShort, title, sourceUrl, playing, onPlay }) {
  const frameRef = useRef(null);
  const [blocked, setBlocked] = useState(false);
  const heardRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!playing) return undefined;
    const onMsg = (e) => {
      if (!YT_ORIGINS.includes(e.origin) || e.source !== frameRef.current?.contentWindow) return;
      heardRef.current = true;
      try {
        const d = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (d?.event === "onError") setBlocked(true);
      } catch (_) {}
    };
    window.addEventListener("message", onMsg);
    return () => { window.removeEventListener("message", onMsg); clearInterval(timerRef.current); };
  }, [playing]);

  const listen = () => {
    heardRef.current = false;
    let tries = 0;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      tries += 1;
      if (heardRef.current || tries > 20) { clearInterval(timerRef.current); return; }
      try { frameRef.current?.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: videoId, channel: "widget" }), "*"); } catch (_) {}
    }, 400);
  };

  const frame = isShort ? "mx-auto aspect-[9/16] h-full max-h-[70vh]" : "aspect-video w-full";
  const origin = encodeURIComponent(window.location.origin);
  return (
    <div className={`flex w-full items-center justify-center bg-black ${isShort ? "h-[70vh] max-h-[680px] py-3" : ""}`} data-testid={isShort ? "short-player" : "video-player-wrap"}>
      <div className={`relative overflow-hidden ${frame} ${isShort ? "rounded-xl" : ""}`}>
        {blocked ? (
          <BlockedNotice url={sourceUrl} reason="This video can't be played inside the app. It may be private, removed, or the owner doesn't allow embedding." />
        ) : playing ? (
          <iframe
            ref={frameRef}
            title={title}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1&enablejsapi=1&origin=${origin}`}
            onLoad={listen}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            data-testid="video-embed"
          />
        ) : (
          <button type="button" onClick={onPlay} className="group absolute inset-0 h-full w-full" aria-label={`Play ${title}`} data-testid="video-poster">
            <img src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`} alt="" className="h-full w-full object-cover opacity-80 transition-opacity group-hover:opacity-100" />
            <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#E30613] shadow-lg transition-transform group-hover:scale-110 group-focus-visible:ring-2 group-focus-visible:ring-white">
              <Play className="ml-1 h-7 w-7 fill-white text-white" />
            </span>
            {isShort && <span className="absolute left-3 top-3 rounded bg-black/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Short</span>}
          </button>
        )}
      </div>
    </div>
  );
}

export function InvalidVideo({ url }) {
  return <BlockedNotice url={url} reason="This video link can't be played here. Open it directly instead." />;
}
